import * as Sentry from "@sentry/browser";
import { type AxiosPromise } from "axios";
import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { FormDatePicker } from "components/DatePicker/SingleDatePicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { HtmlEditor } from "components/ValidatedFields/HtmlEditor";
import moment from "moment";
import "moment-timezone";
import React from "react";
import { Input, Message } from "semantic-ui-react";
import { routes } from "utilities/routing/generatedRoutes";
import { LabelledRow, TimePicker } from "../../components";
import { DeadlineOptionsDropdown } from "./DeadlineOptionsDropdown";
import { formatTimeZone, getTimeZoneFromIana } from "shared/utils/dateTimeUtils";

interface Props {
  config: server.dto.LinkedUserTaskPanelConfig
  existingTask?: server.dto.UserTask
  mainArea: server.dto.MainAreas
  onFinishedSaving: () => void
  onConvertedToNote: () => void

  taskState: TaskState
  onTaskChanged: (taskState: Partial<TaskState>) => void
  canChooseRelativeDeadline: boolean
}

export interface TaskState {
  charactersRemaining: number

  assignedTo: server.dto.UserSmall
  deadlineLocalTimezone: moment.Moment
  isRelativeDeadline: boolean
  offsetDays: number
  visibleToExternal: boolean
  reminderInPopup: boolean
  reminderByEmail: boolean
  subject: string
  body: string
  subjects: string[]

  noUserError: boolean
  noSubjectError: boolean
  bodyTooLongError: boolean
  dateOutsideAllowedRangeError: boolean
  isLoadingNoteConversion: boolean
  isSaving: boolean
}

const minDate = moment().utc().startOf("day");

const TaskCreateModal = ({ canChooseRelativeDeadline, existingTask, mainArea, config, onFinishedSaving, onConvertedToNote, taskState, onTaskChanged }: Props) => {
  const {
    assignedTo,
    deadlineLocalTimezone,
    subject,
    visibleToExternal,
    reminderInPopup,
    reminderByEmail,
    body,
    subjects,
    noUserError,
    noSubjectError,
    bodyTooLongError,
    dateOutsideAllowedRangeError,
    isLoadingNoteConversion,
    isSaving,
    isRelativeDeadline,
    offsetDays
  } = taskState;

  const [errors, setErrors] = React.useState<string[]>([]);

  const isNewTask = !existingTask;

  React.useEffect(() => {
    const listSubjects = async () => {
      const response = await window.CoreApi.FavoriteSubject.ListSubjects();
      if (response.data.hasErrors) {
        return;
      }
      onTaskChanged({ subjects: response.data.data.map(s => s.subjectValue) });
    }
    listSubjects();
  }, []);

  const onDateChange = (date: moment.Moment) => {
    const newMoment = deadlineLocalTimezone.clone();
    newMoment.set("year", date.year());
    newMoment.set("month", date.month());
    newMoment.set("date", date.date());
    onTaskChanged({ deadlineLocalTimezone: newMoment });
  };

  const onTimeChange = (time: moment.Moment) => {
    const newMoment = deadlineLocalTimezone.clone();
    newMoment.set("hour", time.hour());
    newMoment.set("minute", time.minute());
    onTaskChanged({ deadlineLocalTimezone: newMoment });
  };

  const onConvertToNote = async () => {
    const hasNoSubject = !subject || subject.trim().length === 0;
    const bodyIsTooLong = taskState.charactersRemaining < 0;
    if (hasNoSubject || bodyIsTooLong) {
      onTaskChanged({
        noSubjectError: hasNoSubject,
        bodyTooLongError: bodyIsTooLong
      });
      return;
    }

    onTaskChanged({ isLoadingNoteConversion: true });

    await window.CoreApi.UserTask.ConvertToNote({
      userTaskId: existingTask.userTaskId,
      title: subject,
      body,
      visibleToExternal
    });

    onConvertedToNote();
  };

  const onSave = async () => {
    const hasNoSubject = !subject || subject.trim().length === 0;
    const hasNoUser = !assignedTo;
    const bodyIsTooLong = taskState.charactersRemaining < 0;
    const dateOutsideAllowedRangeError = (!isRelativeDeadline) && (deadlineLocalTimezone.isBefore(minDate) || deadlineLocalTimezone.isAfter(moment.utc(serverReference.constants.userTask.maxDeadline)));

    if (hasNoSubject || hasNoUser || dateOutsideAllowedRangeError || bodyIsTooLong) {
      onTaskChanged({
        noUserError: hasNoUser,
        noSubjectError: hasNoSubject,
        bodyTooLongError: bodyIsTooLong,
        dateOutsideAllowedRangeError,
      });
      return;
    }

    onTaskChanged({ isSaving: true });

    let promise: AxiosPromise<server.dto.ApiResponse>;
    const deadline = deadlineLocalTimezone.clone().tz("UTC");

    if (isNewTask) {
      promise = window.CoreApi.UserTask.CreateUserTask({
        mainArea,
        assignedToUserId: assignedTo.userId,
        title: subject,
        body: body ?? "",
        visibleToExternal,
        reminderPopup: reminderInPopup,
        reminderEmail: reminderByEmail,
        parentId: config.itemId,
        deadline: isRelativeDeadline ? undefined : deadline.toDate(),
        deadlineType: isRelativeDeadline ? "relativeToShowDate" : "absolute",
        deadlineOffsetInDays: isRelativeDeadline ? offsetDays : undefined
      });
    } else {
      promise = window.CoreApi.UserTask.UpdateUserTask({
        userTaskId: existingTask.userTaskId,
        assignedToUserId: assignedTo.userId,
        title: subject,
        body,
        visibleToExternal,
        reminderPopup: reminderInPopup,
        reminderEmail: reminderByEmail,
        deadline: isRelativeDeadline ? undefined : deadline.toDate(),
        deadlineType: isRelativeDeadline ? "relativeToShowDate" : "absolute",
        deadlineOffsetInDays: isRelativeDeadline ? offsetDays : undefined
      });
    }

    let errors: string[] = [];
    try {
      const res = await promise;
      errors = res.data.errors.map(e => e.message);
    } catch (ex) {
      errors = [RESX.GeneralLabels.SomethingUnexpectedHappened];
      console.error(ex);
      Sentry.captureException(ex);
    }

    setErrors(errors);
    if (errors.length > 0) {
      onTaskChanged({ isSaving: false });
      return;
    }

    onFinishedSaving();
  };

  const onSubjectChange = (newSubject: string) => {
    onTaskChanged({ subject: newSubject, noSubjectError: false });
  };

  const onAssignedToChanged = (newAssignedTo: server.dto.UserSmall) => {
    onTaskChanged({ assignedTo: newAssignedTo, noUserError: false });
  };

  const displayDatePicker = !isRelativeDeadline || !canChooseRelativeDeadline;
  const tz = getTimeZoneFromIana(deadlineLocalTimezone.tz(), site.culture.uiLocale);
  return (
    <>
      <div className="flex-column gap grow1 scrollbox-auto">
        <table className="s1_table bordergrid gray ">
          <tbody>
            <LabelledRow label={RESX.Message.AssignedTo}>
              <UserModalPicker
                canCreateNew={false}
                nonInPageEditing={{
                  onChange: onAssignedToChanged,
                  value: assignedTo,
                  required: true,
                  legacyHasError: noUserError,
                  validatorKey: "assignedToUserId",
                }}
              />
            </LabelledRow>
            <LabelledRow label={RESX.Message.Deadline}>
              <div className="flex">
                {canChooseRelativeDeadline && <div className="margin-right" style={{ width: 200 }}>
                  <DeadlineOptionsDropdown
                    isRelativeDeadline={isRelativeDeadline}
                    value={offsetDays}
                    onChangeDays={d => { onTaskChanged({ offsetDays: d, isRelativeDeadline: true }); }}
                    onIsNoLongerRelativeDeadline={() => { onTaskChanged({ offsetDays: 0, isRelativeDeadline: false }); }}
                  />
                </div>}
                {displayDatePicker &&
                  <>
                    <div className="margin-right">
                      <FormDatePicker
                        validatorKey="deadlineDate"
                        error={dateOutsideAllowedRangeError}
                        value={deadlineLocalTimezone}
                        onChange={onDateChange}
                        minDate={minDate}
                        maxDate={moment.utc(serverReference.constants.userTask.maxDeadline)}
                      />
                    </div>
                    <div className="margin-right">
                      <TimePicker
                        value={deadlineLocalTimezone}
                        onChange={onTimeChange}
                        canBeEmpty={false}
                        step={15}
                      />
                    </div>
                    <div className="flex-column justify-center">
                      {formatTimeZone(tz)}
                    </div>
                  </>}
              </div>
            </LabelledRow>
            {config.showVisibleToExternalCheckbox && <LabelledRow
              label={RESX.GeneralLabels.Visible}
              bigPadding>
              <Checkbox
                checked={visibleToExternal}
                onChange={() => { onTaskChanged({ visibleToExternal: !visibleToExternal }); }}
                label={RESX.GeneralLabels.VisibleToExternalUsers}
              />
            </LabelledRow>}

            <LabelledRow
              label={RESX.Notifications.resxNotifications}
              bigPadding>
              <div className="margin-bottom-small">
                <Checkbox
                  checked={reminderInPopup}
                  onChange={() => { onTaskChanged({ reminderInPopup: !reminderInPopup }); }}
                  label={RESX.Message.ReminderInPopup}
                />
              </div>
              <div>
                <Checkbox
                  checked={reminderByEmail}
                  onChange={() => { onTaskChanged({ reminderByEmail: !reminderByEmail }); }}
                  label={RESX.GeneralLabels.Email}
                />
              </div>
            </LabelledRow>

            <LabelledRow label={RESX.Message.Subject}>
              <div className="flex">
                <div className="grow1 subjects padding-small">
                  {subjects.map((subject, idx) =>
                    <a onClick={() => { onSubjectChange(subject); }} key={subject + idx}>
                      {subject}
                    </a>
                  )}
                </div>
                <div className="padding-small">
                  <a href={routes.personal_FavoriteSubject_List().absolute} className="icon-cog"></a>
                </div>
                <div className="padding-small" style={{
                  fontSize: "large",
                  color: bodyTooLongError ? "var(--error-color)" : "var(--text-color-light)",
                }}>
                  {taskState.charactersRemaining}
                </div>
              </div>
              <div>
                <Input
                  value={subject}
                  onChange={(_, { value }) => { onSubjectChange(value); }}
                  fluid
                  error={noSubjectError}
                  maxLength={serverReference.constants.userTask.maxTitleLength}
                />
              </div>
            </LabelledRow>
          </tbody>
        </table>

        <HtmlEditor
          toolbar="Normal"
          content={body}
          maxContentLength={serverReference.constants.userTask.maxBodyLength}
          onChange={(newContent, charactersRemaining) => {
            onTaskChanged({
              body: newContent,
              bodyTooLongError: false,
              charactersRemaining,
            })
          }}
        />
      </div>

      <div className="flex gap">
        <div className="grow1">
          {errors.length > 0 && <Message className="margin-top" color="red">
            {errors.map((userTaskError) =>
              <div key={userTaskError}>{userTaskError}</div>
            )}
          </Message>}
        </div>
        {(!isNewTask) && <div className="margin-right">
          <ButtonSecondary
            loading={isLoadingNoteConversion}
            disabled={isSaving || isLoadingNoteConversion}
            onClick={onConvertToNote}
            label={RESX.Message.ConvertToNote} />
        </div>}
        <div>
          <ButtonPrimary
            loading={isSaving}
            disabled={isSaving || isLoadingNoteConversion}
            onClick={onSave}
            label={RESX.Buttons.btnSave} />
        </div>
      </div >
    </>
  );
};

export default TaskCreateModal;